import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Sku, SkuInput, ListSkusFilters,
  listSkus, getSku, createSku, updateSku, deleteSku,
} from '../services/skusApi';

interface SkusState {
  items: Sku[];
  current: Sku | null;
  pagination: { total: number; page: number; limit: number; pages: number } | null;
  filters: ListSkusFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;
  setFilters: (patch: Partial<ListSkusFilters>) => void;
  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<Sku>;
  create: (input: SkuInput) => Promise<Sku>;
  update: (id: number | string, input: Partial<SkuInput>) => Promise<Sku>;
  remove: (id: number | string) => Promise<void>;
}

const defaultFilters: ListSkusFilters = { page: 1, limit: 50 };
const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useSkusStore = create<SkusState>()(
  devtools(
    (set, get) => ({
      items: [],
      current: null,
      pagination: null,
      filters: { ...defaultFilters },
      loading: false,
      saving: false,
      error: null,
      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, page: 1 } }),
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const result = await listSkus(get().filters);
          set({ items: result.data, pagination: result.pagination, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const sku = await getSku(id);
          set({ current: sku, loading: false });
          return sku;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createSku(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateSku(id, input);
          set((s) => ({
            items: s.items.map((it) => (String(it.id) === String(id) ? updated : it)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      remove: async (id) => {
        set({ saving: true, error: null });
        try {
          await deleteSku(id);
          set((s) => ({ items: s.items.filter((it) => String(it.id) !== String(id)), saving: false }));
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'skus-store' }
  )
);
